﻿import { formatDate } from '../../../../Foundation/utils';
import { createIcon } from '../../icons';
export class SelectedItemsSidebar {
  /**
   * Right side slide-in panel showing items selected in a ServiceResultsTable.
   * Opens by clicking on the next-step-bar "view selected items" button.
   * The sidebar listens for custom event "selected-items:open" dispatched on document.
   */
  constructor(element) {
    this.el = element;

    this._refs = {
      closeBtn: element.querySelector('[data-sidebar-close]'),
      deleteAllBtn: element.querySelector('[data-sidebar-delete-all]'),
      toolbar: element.querySelector('[data-sidebar-delete-all-toolbar]'),
      list: element.querySelector('[data-sidebar-list]'),
      heading: element.querySelector('[data-sidebar-heading]'),
      emptyState: element.querySelector('[data-sidebar-empty]'),
      startSearchBtn: element.querySelector('[data-sidebar-start-search]'),
    };

    this._tableInstance = null;
    this._config = this._parseConfig();
    this._tableConfig = this._parseTableConfig();

    this._addEventListeners();
  }

  _addEventListeners() {
    document.addEventListener('selected-items:open', async (e) => {
      this._tableInstance = e.detail?.tableInstance || null;

      if (this._tableInstance?.fetchImages) {
        await this._tableInstance.fetchImages();
      }

      this._render();

      if (!this._isOpen()) {
        this._open();
      }
    });

    if (this._refs.closeBtn) {
      this._refs.closeBtn.addEventListener('click', () => this._close());
    }

    if (this._refs.deleteAllBtn) {
      this._refs.deleteAllBtn.addEventListener('click', () => this._deleteAll());
    }

    if (this._refs.startSearchBtn) {
      this._refs.startSearchBtn.addEventListener('click', () => {
        this._close();
        this._tableInstance?.resetForNewSearch?.();
      });
    }

    this.el.addEventListener('click', (e) => {
      if (e.target === this.el || e.target.classList.contains('selected-items-sidebar__overlay'))
        this._close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen()) this._close();
    });
  }

  _parseConfig() {
    try {
      const configData = this.el.getAttribute('data-selected-items-sidebar-config');
      if (!configData || configData.trim() === '') return {};
      return JSON.parse(configData);
    } catch (e) {
      console.error('SelectedItemsSidebar: config is not valid JSON!', e);
      return {};
    }
  }

  _parseTableConfig() {
    try {
      const configData = this.el.getAttribute('data-selected-items-table-config');
      if (!configData || configData.trim() === '') return {};
      return JSON.parse(configData);
    } catch (e) {
      console.error('SelectedItemsSidebar: table config is not valid JSON!', e);
      return {};
    }
  }

  _getTableConfig() {
    return this._tableInstance?._config || this._tableConfig || {};
  }

  _isOpen() {
    return this.el.getAttribute('aria-hidden') === 'false';
  }

  _open() {
    // Compensate for scrollbar disappearing when overflow:hidden is applied - preventing layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.paddingRight = `${scrollbarWidth}px`;
    this.el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sidebar-open');

    if (this._refs.closeBtn) {
      this._refs.closeBtn.focus();
    }
  }

  _close() {
    this.el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sidebar-open');
    document.body.style.paddingRight = '';
    document.dispatchEvent(new CustomEvent('selected-items:closed'));
  }

  _deleteAll() {
    if (!this._tableInstance) return;

    const serials = [...this._tableInstance._state.selectedItemsData.keys()];

    serials.forEach((serialNo) => {
      this._uncheckSerialInTable(serialNo);
      this._tableInstance.removeSelectedItemData?.(serialNo);
    });

    this._tableInstance._updateSelectAllState?.();
    this._tableInstance._updateNextStepBar();
    this._render();
  }

  _uncheckSerialInTable(serialNo) {
    const checkboxes = this._tableInstance._refs.tbody.querySelectorAll(
      `.service-results-table__checkbox[value="${CSS.escape(
        serialNo,
      )}"]:not(.service-results-table__checkbox--mobile)`,
    );

    checkboxes.forEach((cb) => {
      cb.checked = false;
      const row = cb.closest('tr');

      if (row) {
        row.querySelectorAll('.service-results-table__checkbox').forEach((c) => {
          c.checked = false;
        });
      }
    });
  }

  _removeItem(serialNo) {
    if (!this._tableInstance) return;

    this._uncheckSerialInTable(serialNo);

    this._tableInstance.removeSelectedItemData?.(serialNo);

    this._tableInstance._updateSelectAllState?.();
    this._tableInstance._updateNextStepBar();
    this._render();
  }

  _getSelectedItems() {
    if (!this._tableInstance) return [];

    return this._tableInstance.getSelectedItemsData().map((item) => ({
      ...item,
      warrantyLabel:
        item.warrantyLabel || this._config.warrantyLabel || this._config.repairWarrantyLabel,
    }));
  }

  _render() {
    const items = this._getSelectedItems();
    const { list, heading, emptyState } = this._refs;

    if (heading) {
      const baseText = this._config.headerText || '';
      const itemsLabel = this._config.itemsLabel || 'items';
      heading.textContent = `${baseText} (${items.length} ${itemsLabel})`;
    }

    if (list) {
      list.innerHTML = '';

      items.forEach((item) => {
        list.appendChild(this._createCard(item));
      });
    }

    const isEmpty = !items.length;
    const shouldShowDeleteAll = items.length >= 2;

    if (emptyState) emptyState.setAttribute('aria-hidden', String(!isEmpty));
    if (list) list.setAttribute('aria-hidden', String(isEmpty));
    if (this._refs.toolbar) {
      this._refs.toolbar.setAttribute('aria-hidden', String(!shouldShowDeleteAll));
    }
  }

  _createCard(item) {
    const card = document.createElement('div');
    card.className = 'selected-items-sidebar__card';

    const imageEl = document.createElement('div');
    imageEl.className = 'selected-items-sidebar__card-image';

    if (item.imageUrl && item.imageUrl.trim() !== '') {
      const img = document.createElement('img');
      img.src = item.imageUrl;
      img.alt = item.description || '';

      img.addEventListener('error', () => {
        img.remove();
        imageEl.classList.add('selected-items-sidebar__card-image--empty');
      });

      imageEl.appendChild(img);
    } else {
      imageEl.classList.add('selected-items-sidebar__card-image--empty');
    }

    const body = document.createElement('div');
    body.className = 'selected-items-sidebar__card-body';

    const description = document.createElement('p');
    description.className = 'selected-items-sidebar__card-description';
    description.textContent = item.description || '-';
    body.appendChild(description);

    const serialNoLabel = this._config.serialNoLabel || 'Serial no.';
    const patientLabel = this._config.patientNameLabel || 'Patient name';
    const warrantyLabel =
      item.warrantyLabel || this._config.warrantyLabel || this._config.repairWarrantyLabel || '';

    body.appendChild(this._createCardRow(serialNoLabel, item.serialNo));
    body.appendChild(this._createCardRow(patientLabel, item.patient || '-'));
    body.appendChild(this._createWarrantyRow(warrantyLabel, item.warrantyDate));

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'selected-items-sidebar__card-remove';
    removeBtn.setAttribute('aria-label', `Remove ${item.serialNo}`);

    removeBtn.appendChild(createIcon('remove'));

    removeBtn.addEventListener('click', () => this._removeItem(item.serialNo));

    card.appendChild(imageEl);
    card.appendChild(body);
    card.appendChild(removeBtn);

    return card;
  }

  _createCardRow(label, value) {
    const row = document.createElement('div');
    row.className = 'selected-items-sidebar__card-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'selected-items-sidebar__card-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'selected-items-sidebar__card-value';
    valueEl.textContent = value;

    row.appendChild(labelEl);
    row.appendChild(valueEl);

    return row;
  }

  _createWarrantyRow(label, rawDate) {
    const row = this._createCardRow(label, '');
    const valueEl = row.querySelector('.selected-items-sidebar__card-value');

    const date = rawDate ? new Date(rawDate) : null;
    const hasValidDate = date && !isNaN(date.getTime()) && date.getFullYear() >= 1900;

    if (!hasValidDate) {
      valueEl.textContent = '-';
      return row;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const isExpired = date < today;

    valueEl.classList.add('selected-items-sidebar__card-value--warranty');
    const tableConfig = this._getTableConfig();
    valueEl.textContent = formatDate(
      rawDate,
      tableConfig.DateFormat || tableConfig.dateFormat || 'dd-mm-yyyy',
    );
    valueEl.classList.add(
      isExpired
        ? 'selected-items-sidebar__card-value--expired'
        : 'selected-items-sidebar__card-value--valid',
    );

    return row;
  }
}
